import Phaser from "phaser";
import { COLORS } from "../constants";
import { PrimitiveIcon } from "../avatars/PrimitiveIcon";
import { PrimitiveIconId } from "../avatars/icons/primitiveIcons";

/**
 * Intent indicator component inspired by Final Fantasy games.
 * Shows what action an enemy will take with a stylized icon + value display.
 *
 * Visual design:
 * - Sword icon for attack intent, shield icon for defend
 * - Value displayed prominently (damage for attack, armor for defend)
 * - Subtle backdrop for readability
 * - Pulse animation when active
 */
export type IntentType = "attack" | "defend";

export type IntentIndicatorOptions = {
  /** The damage/armor value to display */
  value: number;
  /** The type of intent (attack or defend) */
  intentType?: IntentType;
  /** Whether this enemy is actively performing this action (shows enhanced visuals) */
  isActive?: boolean;
  /** Scale multiplier (default 1) */
  scale?: number;
};

const INDICATOR_WIDTH = 40; // Compact width for horizontal layout
const INDICATOR_HEIGHT = 20; // Reduced height
const FONT_SIZE = 14; // Smaller font for compact display

// Colors for different intent types
const INTENT_COLORS = {
  attack: {
    accent: COLORS.damageText,
    text: COLORS.damageText,
  },
  defend: {
    accent: "#60a5fa", // Blue for defense
    text: "#60a5fa",
  },
};

export class IntentIndicator {
  private root: Phaser.GameObjects.Container;
  private backdrop: Phaser.GameObjects.Graphics;
  private icon?: PrimitiveIcon;
  private valueText: Phaser.GameObjects.Text;
  private glowGraphics: Phaser.GameObjects.Graphics;
  private pulseTween?: Phaser.Tweens.Tween;
  private isActive = false;
  private intentType: IntentType = "attack";

  constructor(
    private scene: Phaser.Scene,
    options?: Partial<IntentIndicatorOptions>
  ) {
    const scale = options?.scale ?? 1;
    this.intentType = options?.intentType ?? "attack";

    this.root = scene.add.container(0, 0);
    this.root.setSize(INDICATOR_WIDTH * scale, INDICATOR_HEIGHT * scale);
    this.root.setScale(scale);

    // Glow effect (rendered behind backdrop)
    this.glowGraphics = scene.add.graphics();
    this.root.add(this.glowGraphics);

    // Backdrop - dark semi-transparent panel
    this.backdrop = scene.add.graphics();
    this.root.add(this.backdrop);

    // Value text
    const colors = INTENT_COLORS[this.intentType];
    this.valueText = scene.add
      .text(0, 0, "", {
        fontFamily: "Arial, sans-serif",
        fontSize: `${FONT_SIZE}px`,
        color: colors.text,
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    this.root.add(this.valueText);

    // Draw initial state
    this.drawBackdrop();
    this.createIcon();

    if (options?.value !== undefined) {
      this.setValue(options.value);
    }
    if (options?.isActive) {
      this.setActive(true);
    }
  }

  private drawBackdrop() {
    const g = this.backdrop;
    g.clear();
    // Minimal background - removed border and dark backdrop for a cleaner look
  }

  private drawGlow() {
    const g = this.glowGraphics;
    g.clear();

    if (!this.isActive) return;

    const glowColorStr = INTENT_COLORS[this.intentType].accent;
    const glowColor = Phaser.Display.Color.HexStringToColor(glowColorStr).color;

    // Outer glow layers
    g.fillStyle(glowColor, 0.08);
    g.fillRoundedRect(-4, -4, INDICATOR_WIDTH + 8, INDICATOR_HEIGHT + 8, 10);
    g.fillStyle(glowColor, 0.12);
    g.fillRoundedRect(-2, -2, INDICATOR_WIDTH + 4, INDICATOR_HEIGHT + 4, 8);
  }

  private createIcon() {
    // Destroy existing icon if present
    if (this.icon) {
      this.icon.destroy();
      this.icon = undefined;
    }

    const iconId: PrimitiveIconId = this.intentType === "defend" ? "defend" : "attack";
    const iconSize = 16; // Size for compact indicator
    const iconScale = iconSize / 32; // Icons are designed at 32x32

    this.icon = new PrimitiveIcon(this.scene, iconId, {
      scale: iconScale,
    });

    // Position icon on the left side - account for centering in setPosition
    const iconX = 5 + iconSize / 2; // Left edge at 5, center at 5 + iconSize/2
    const iconY = INDICATOR_HEIGHT / 2;
    this.icon.setPosition(iconX, iconY);

    this.root.add(this.icon.getRoot());
  }

  setValue(value: number) {
    this.valueText.setText(String(value));
    // Position text to the right of the icon, centered vertically
    const textX = INDICATOR_WIDTH / 2 + 8;
    const textY = INDICATOR_HEIGHT / 2;
    this.valueText.setPosition(textX, textY);
  }

  setIntent(intentType: IntentType, value: number) {
    this.intentType = intentType;
    const colors = INTENT_COLORS[intentType];

    // Update text color
    this.valueText.setColor(colors.text);

    // Recreate icon with new intent type
    this.createIcon();
    this.drawBackdrop();
    this.drawGlow();

    this.setValue(value);
  }

  setActive(active: boolean) {
    this.isActive = active;

    this.drawGlow();

    // Clear existing pulse tween
    if (this.pulseTween) {
      this.pulseTween.stop();
      this.pulseTween = undefined;
      this.root.setScale(1);
    }

    const colors = INTENT_COLORS[this.intentType];

    if (active) {
      // Pulse animation for active state
      this.pulseTween = this.scene.tweens.add({
        targets: this.root,
        scaleX: 1.08,
        scaleY: 1.08,
        duration: 500,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });

      // Text color becomes brighter white when active
      this.valueText.setColor("#ffffff");
      this.valueText.setShadow(0, 0, colors.accent, 4, true, true);
    } else {
      // Reset to default styling based on intent type
      this.valueText.setColor(colors.text);
      this.valueText.setShadow(0, 0, undefined, 0);
    }
  }

  setPosition(x: number, y: number) {
    this.root.setPosition(x, y);
  }

  setVisible(visible: boolean) {
    this.root.setVisible(visible);
  }

  getRoot(): Phaser.GameObjects.Container {
    return this.root;
  }

  getSize(): { width: number; height: number } {
    return { width: INDICATOR_WIDTH, height: INDICATOR_HEIGHT };
  }

  destroy() {
    this.pulseTween?.stop();
    this.icon?.destroy();
    this.root.destroy(true);
  }

  /**
   * Play entrance animation (slide + fade in from above)
   */
  playEntranceAnimation() {
    const originalY = this.root.y;
    this.root.setAlpha(0);
    this.root.y = originalY - 10;

    this.scene.tweens.add({
      targets: this.root,
      y: originalY,
      alpha: 1,
      duration: 250,
      ease: "Back.easeOut",
    });
  }

  /**
   * Static method to calculate the height this indicator needs
   */
  static getHeight(): number {
    return INDICATOR_HEIGHT;
  }

  static getWidth(): number {
    return INDICATOR_WIDTH;
  }
}
